import { Button, Form, message, Modal, Space, Tooltip } from 'antd';
import {
  ContainerOutlined,
  DollarCircleOutlined,
  ExclamationCircleOutlined,
  PayCircleOutlined,
} from '@ant-design/icons';
import { useQuery, useLazyQuery, useMutation } from '@apollo/client';
import { useEffect, useState } from 'react';
import { PageContext } from '../../../lib/context';
import mutations from '../../../utils/gql/mutations';
import queries from '../../../utils/gql/queries';
import BillingsPage from './billings';
import moment from 'moment';
import auth from '../../../lib/services';

interface Sort {
  id: string | null | boolean;
  subscriber: Subscriber;
  consumption: string | null | boolean;
  total_balance: string | null | boolean;
  previous_balance: string | null | boolean;
  advance_payment: string | null | boolean;
}

interface Subscriber {
  id?: string | null | boolean;
  first_name?: string | null | boolean;
  complete_address?: string | null | boolean;
}

const BillerController = () => {
  const [getBillTable, { data: bill_table, loading: bill_table_loading }] =
    useLazyQuery(queries.GET_CUSTOMER_BILLINGS, {
      fetchPolicy: 'no-cache',
    });
  const [total, setTotal] = useState<number>();
  const [limit, setLimit] = useState<number>(10);
  const [offset, setOffset] = useState<number>(0);
  const [current, setCurrent] = useState<number>(1);
  const [billingsTable, setBillingsTable] = useState<any[]>([]);
  const [active, setActive] = useState<boolean>(true);
  const [show, setShow] = useState<boolean>(false);
  const [visible, setVisible] = useState<boolean>(false);
  const [billingInfo, setBillingInfo] = useState(null);
  const [searchBill, setSearchBill] = useState(null);
  const [info, setInfo] = useState(null);
  const [settingsInfo, setSettingsInfo] = useState(null);
  const [displayAmount, setDisplayAmount] = useState<boolean>(true);
  const [selectedBill, setSelectedBill] = useState<any>();
  const [showDrawer, setShowDrawer] = useState<boolean>(false);
  const [graphDatas, setGraphDatas] = useState({ labels: [], data: [] });
  const [csv, setCsv] = useState<any[]>([]);
  const [sort, setSort] = useState<any>(null);
  const [sortOrder, setSortOrder] = useState<Sort>({
    id: false,
    subscriber: {
      id: false,
      first_name: false,
      complete_address: false,
    },
    consumption: false,
    total_balance: false,
    previous_balance: false,
    advance_payment: false,
  });

  const { data: billings_total } = useQuery(queries.GET_BILLINGS_TOTAL, {
    fetchPolicy: 'no-cache',
  });

  const { data: billing_csv } = useQuery(queries.GET_BILLING_CSV, {
    fetchPolicy: 'no-cache',
  });

  const [getBilling, { data: billing_data }] = useLazyQuery(
    queries.BILLING_INFO,
    {
      fetchPolicy: 'no-cache',
    }
  );

  const { data: settings_data } = useQuery(queries.GET_SETTINGS, {
    fetchPolicy: 'no-cache',
  });

  const [getPayments, { data: payments_data }] = useLazyQuery(
    queries.GET_PAYMENTS,
    {
      fetchPolicy: 'no-cache',
    }
  );

  const handleSort = (pagination: any, filters: any, sorter: any) => {
    const sorterOrder: any = {
      ascend: 'asc',
      descend: 'desc',
    };
    if (sorter.columnKey === 'name') {
      setSort({
        subscriber: {
          first_name: sorterOrder[sorter.order],
        },
      });
      setSortOrder({
        ...sortOrder,
        subscriber: {
          first_name: sorter.order,
        },
      });
    } else if (sorter.columnKey === 'address') {
      setSort({
        subscriber: {
          complete_address: sorterOrder[sorter.order],
        },
      });
      setSortOrder({
        ...sortOrder,
        subscriber: {
          complete_address: sorter.order,
        },
      });
    } else if (sorter.columnKey === 'subscriber_id') {
      setSort({
        subscriber: {
          id: sorterOrder[sorter.order],
        },
      });
      setSortOrder({
        ...sortOrder,
        subscriber: {
          id: sorter.order,
        },
      });
    } else {
      setSort({
        [sorter.columnKey]: sorterOrder[sorter.order],
      });
      setSortOrder({ ...sortOrder, [sorter.columnKey]: sorter.order });
    }
  };

  const [insertPayments] = useMutation<any>(mutations.INSERT_PAYMENTS, {
    onCompleted(res) {
      message.success({ content: 'Payment has been received successfully ' });
      _getBilling();
      form.resetFields();
      _closeDrawer();
    },
    onError({ graphQLErrors }) {
      message.error('Something is wrong.');
    },
  });

  const usersColumns = [
    {
      title: 'Bill ID',
      key: 'id',
      render: (item: any) => {
        return subs_id(item.id);
      },
      sorter: true,
      sortOrder: sortOrder.id,
    },
    {
      title: 'Customer ID',
      key: 'subscriber_id',
      render: (item: any) => {
        return subs_id(item.subscriber.id);
      },
      sorter: true,
      sortOrder: sortOrder.subscriber?.id,
    },
    {
      title: 'Name',
      key: 'name',
      render: (item: any) => {
        return `${item.subscriber.first_name}  ${item.subscriber.last_name}`;
      },
      sorter: true,
      sortOrder: sortOrder.subscriber?.first_name,
    },
    {
      title: 'Address',
      key: 'address',
      render: (item: any) => {
        return item.subscriber.complete_address;
      },
      sorter: true,
      sortOrder: sortOrder.subscriber?.complete_address,
    },
    {
      title: 'Consumption',
      dataIndex: 'consumption',
      key: 'consumption',
      sorter: true,
      sortOrder: sortOrder.consumption,
    },

    {
      title: 'Bill',
      key: 'total_balance',
      render: (item: any) =>
        Math.round(item.total_balance * 100) / 100 < 0
          ? 0
          : Math.round(item.total_balance * 100) / 100,
      sorter: true,
      sortOrder: sortOrder.total_balance,
      // render: (val: any) => {
      //   const { total_bill, total_payment } = val;
      //   const bill = total_bill?.total_balance;
      //   const paid = total_payment?.payments_aggregate?.aggregate?.sum?.amount;
      //   if (bill && paid) {
      //     const total = bill - paid;
      //     return total.toLocaleString();
      //   }
      //   return 0;
      // },
    },
    {
      title: 'Status',
      render: (item: any) => {
        if (item.total_balance > item.payments_aggregate.aggregate.sum.amount) {
          return 'Unpaid';
        }
        return 'Paid';
      },
      sorter: (a: any, b: any) =>
        `${a.total_balance} ${a.payments_aggregate.aggregate.sum.amount}`.localeCompare(
          `${b.total_balance} ${b.payments_aggregate.aggregate.sum.amount}`
        ),
    },
    {
      title: 'Previous Payment',
      key: 'previous_balance',
      render: (item: any) => Math.round(item?.previous_balance * 100) / 100,
      sorter: true,
      sortOrder: sortOrder.previous_balance,
    },

    {
      title: 'Advance Payment',
      key: 'advance_payment',
      render: (item: any) => Math.round(item?.advance_payment * 100) / 100,
      sorter: true,
      sortOrder: sortOrder.advance_payment,
    },

    {
      title: 'Action',
      key: 'action',
      align: 'center',
      render: (record: any) => (
        <Space>
          <Tooltip title="Show Bills">
            <Button
              type="link"
              onClick={() => {
                getBilling({
                  variables: {
                    subs_id: record.id,
                  },
                });

                getGraphData({
                  variables: {
                    id: record?.subscriber.id,
                  },
                });
                setInfo(record);
              }}
              icon={<ContainerOutlined />}
              size="middle"
              style={{ width: 50 }}
            ></Button>
          </Tooltip>
          <Tooltip title="Pay Bills">
            <Button
              onClick={() => _showPayBill(record)}
              type="link"
              icon={<DollarCircleOutlined />}
              size="middle"
              style={{ width: 50 }}
            ></Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const [form] = Form.useForm();
  const handleAmount = (record: any) => {
    if (
      record?.total_balance &&
      record?.total_balance > record?.payments_aggregate?.aggregate?.sum?.amount
    ) {
      setDisplayAmount(true);
    } else {
      setDisplayAmount(false);
    }
  };

  const _getPayments = () => {
    getPayments();
  };

  const _showHandler = () => setShow(true);
  const _closeHandler = () => setShow(false);
  const _switchHandler = () => {
    setActive(!active);
  };

  const _submitHandler = () => {
    form.submit();
  };

  useEffect(() => {
    if (bill_table) {
      setBillingsTable(bill_table.view_billing_csv);
      getBilling();
    }
  }, [bill_table]);

  useEffect(() => {
    if (billing_data) {
      const data = billing_data.billings[0];
      setBillingInfo(data);
      setVisible(true);
    }
  }, [billing_data]);

  const _getBilling = () => {
    getBilling();
  };

  useEffect(() => {
    _getBilling();
  }, [getBilling]);

  useEffect(() => {
    if (settings_data) {
      const data = settings_data.settings;
      setSettingsInfo(data);
    }
  }, [settings_data]);

  const subs_id = (id: number): String => {
    var sub_id = `${id}`;
    while (sub_id.length < 7) {
      sub_id = `0${sub_id}`;
    }
    return sub_id;
  };

  const [getGraphData, { data: graph_data }] = useLazyQuery(
    queries.GET_GRAPH_BILL_DATA,
    {
      fetchPolicy: 'no-cache',
    }
  );

  useEffect(() => {
    if (graph_data) {
      setGraphDatas({
        labels: graph_data.billings.map(({ billing_date }: any) =>
          moment(billing_date).format('MMM')
        ),
        data: graph_data.billings.map(
          ({ total_balance }: any) => Math.round(total_balance * 100) / 100
        ),
      });
    }
  }, [graph_data]);

  const graphData = {
    labels: graphDatas.labels,
    datasets: [
      {
        label: 'Monthly Bills',
        data: graphDatas.data,
        backgroundColor: [
          'rgba(255, 99, 132, 0.2)',
          'rgba(255, 206, 86, 0.2)',
          'rgba(54, 162, 235, 0.2)',
        ],
      },
    ],
  };

  useEffect(() => {
    if (billings_total) {
      const temp = billings_total.view_billing_csv_aggregate;
      setTotal(temp?.aggregate.count);
    }
  }, [billings_total]);
  // const [getSearchBill, { data: search_bill, loading: search_bill_loading }] = useLazyQuery(
  //   queries.GET_CUSTOMER_BILLINGS,
  //   {
  //     fetchPolicy: 'no-cache',
  //   }
  // );

  // useEffect(() => {
  //   if (search_bill) {
  //     const data = search_bill.billings;
  //     setSearchBill(data);
  //     setBillingsTable(data);
  //   }
  // }, [search_bill]);

  useEffect(() => {
    _getBillTable();
  }, [sort]);

  const _getBillTable = () => {
    getBillTable({
      variables: {
        limit: limit,
        offset: offset,
        order_by: sort,
      },
    });
  };

  useEffect(() => {
    _getBillTable();
  }, [getBillTable]);

  const _onFinishSearch = (data: any) => {
    let where: any = {};
    if (data.id) {
      where = {
        ...where,
        _or: [
          {
            id: { _eq: data?.id ? parseInt(data.id) : null },
          },
          {
            account_id: { _eq: data?.id ? parseInt(data.id) : null },
          },
        ],
      };
    }
    setBillingsTable([]);
    getBillTable({
      variables: {
        where,
        limit: limit,
        offset: offset,
        order_by: sort,
      },
    });
  };

  const [payments_form] = Form.useForm();
  const _showPayBill = (data: any) => {
    setShowDrawer(true);
    setSelectedBill(data);
    setInfo(data);
    payments_form.resetFields();
    payments_form.setFieldsValue({
      subscriber_id: data?.subscriber?.id,
      billing_id: data?.id,
      payment_id: data?.payments?.id,
    });
  };

  const { confirm } = Modal;
  const _onFinishPay = (data: any) => {
    confirm({
      title: (
        <span>
          <span>A payment of </span>
          <span className="font-bold">Php {data.amount} </span>
          <span>has been received. Please confirm to proceed.</span>
        </span>
      ),
      icon: <ExclamationCircleOutlined />,
      onOk() {
        insertPayments({
          variables: {
            data: {
              amount: data.amount,
              billing_id: data.billing_id,
              subscribers_id: data.subscriber_id,
              date: moment().format(),
              transacted_by: auth.getUserInfo().id,
            },
          },
        });
      },
    });
    form.resetFields();
  };

  const _closeDrawer = () => {
    setShowDrawer(false);
  };

  useEffect(() => {
    if (billing_csv) {
      const header = [
        [
          'ID',
          'Name',
          'Account ID',
          'Meter ID',
          'Mobile number',
          'Consumer type',
          'Previous reading',
          'Previous balance',
          'Current reading',
          'Consumption',
          'Advance payment',
          'Tax',
          'Cutoff date',
          'Additional fee title',
          'Additional fee amount',
          'Total balance',
          'Water fee',
        ],
      ];
      const items = billing_csv.view_billing_csv.map((item: any) => {
        const values = [];
        values.push(item?.id);
        values.push(item?.name);
        values.push(item?.account_id);
        values.push(item?.meter_id);
        values.push(item?.mobile_number);
        values.push(item?.consumer_type);
        values.push(item?.previous_reading);
        values.push(item?.previous_balance);
        values.push(item?.current_reading);
        values.push(item?.consumption);
        values.push(item?.advance_payment);
        values.push(item?.tax);
        values.push(moment(item?.cutoff_date).format('LLL'));
        values.push(item?.additional_fee_title);
        values.push(item?.additional_fee_amount);
        values.push(item?.total_balance);
        values.push(item?.water_fee);
        return values;
      });
      setCsv([...header, ...items]);
    }
  }, billing_csv);

  const handlePaginationChange = (page: any, pageSize: any) => {
    setLimit(pageSize);
    setCurrent(page);
    if (page * pageSize > pageSize) {
      setOffset(page * pageSize - pageSize);
    }
    if (page === 1) {
      setCurrent(page);
      setOffset(0);
    }
  };

  useEffect(() => {
    if (offset || current) {
      _getBillTable();
    }
  }, [offset, current]);

  const value: object = {
    graphData,
    form,
    show,
    billingsTable,
    usersColumns,
    visible,
    billingInfo,
    info,
    settingsInfo,
    searchBill,
    selectedBill,
    payments_form,
    showDrawer,
    csv,
    // search_bill_loading,
    bill_table_loading,
    limit,
    offset,
    total,
    current,
    handleSort,
    handlePaginationChange,
    subs_id,
    _showHandler,
    _closeHandler,
    _switchHandler,
    _submitHandler,
    // _onFinish,
    setVisible,
    handleAmount,
    _onFinishSearch,
    _onFinishPay,
    _closeDrawer,
  };

  return (
    <PageContext.Provider value={value}>
      <BillingsPage />
    </PageContext.Provider>
  );
};

export default BillerController;
